import { emeAddToCart } from '@theme/eme-cart';

/**
 * Studio product card:
 * - image carousel (swipe on touch, arrows on hover)
 * - size buttons add straight to the cart
 * - on touch screens the plus opens a size sheet (or adds a single-variant product)
 */
class EmeProductCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get #sheet() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('.eme-card__sheet'));
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const arrow = /** @type {HTMLElement | null} */ (target.closest('[data-slide]'));
    if (arrow) {
      event.preventDefault();
      this.#slide(Number(arrow.dataset.slide));
      return;
    }

    if (target.closest('[data-open-sheet]')) {
      event.preventDefault();
      this.#sheet?.showModal();
      return;
    }

    // A tap on the sheet backdrop closes it
    if (target === this.#sheet) {
      this.#sheet?.close();
      return;
    }

    const button = /** @type {HTMLButtonElement | null} */ (target.closest('button[data-variant-id]'));
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    event.preventDefault();
    button.setAttribute('aria-busy', 'true');

    try {
      await emeAddToCart(button.dataset.variantId ?? '', 1, this);
      this.#sheet?.close();
      this.#flashAdded();
    } catch (error) {
      console.error(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  /** @param {number} direction */
  #slide(direction) {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    if (!track) return;

    const width = track.clientWidth;
    const max = track.scrollWidth - width;
    let next = Math.round(track.scrollLeft / width) * width + direction * width;

    // Loop around at both ends
    if (next > max + 1) next = 0;
    if (next < -1) next = max;

    track.scrollTo({ left: next, behavior: 'smooth' });
  }

  #flashAdded() {
    const label = this.querySelector('.eme-card__add .eme-card__add-label');
    if (!label) return;

    const original = label.textContent;
    label.textContent = window.Theme?.translations?.added || 'Added';
    setTimeout(() => {
      label.textContent = original;
    }, 1500);
  }
}

if (!customElements.get('eme-product-card')) {
  customElements.define('eme-product-card', EmeProductCard);
}
